/**
 * Submission validation utilities for task submissions.
 * These functions handle validation logic that was previously embedded in components.
 */

import React from 'react';
import {
  VStack,
  HStack,
  Text,
  Icon,
} from '@gluestack-ui/themed';
import { AlertCircle } from 'lucide-react-native';
import { referenceSectionStartIndex, validReferencesHeadlineTexts, wordCountBounds } from './wordCount';

/**
 * Validates if the submission text contains images (which are not allowed)
 * @param {string} text - HTML text to validate
 * @returns {boolean} True if text contains images
 */
export const containsImages = (text) => text.includes('<img');

/**
 * Splits submission into body and references section
 * @param {string} text - HTML text to split
 * @returns {Object} { body, refs, headline }
 */
export const splitSubmission = (text) => {
  if (!text) return { body: '', refs: '', headline: null };

  const splitIndex = referenceSectionStartIndex(text);
  if (splitIndex === -1) {
    return { body: text, refs: '', headline: null };
  }

  let headlineFound = null;
  validReferencesHeadlineTexts.forEach((headline) => {
    const regex = new RegExp(`<h[1-3][^>]*>\\s*${headline}\\s*</h[1-3]>`, 'i');
    const match = text.match(regex);
    if (match && match.index === splitIndex) {
      headlineFound = headline;
    }
  });

  return {
    body: text.substring(0, splitIndex),
    refs: text.substring(splitIndex),
    headline: headlineFound,
  };
};

/**
 * Validates wordcount against formal criteria limits
 * @param {number} bodyWords - Current body word count
 * @param {Object} formalCriteria - Formal criteria object
 * @returns {Object} Validation result with isValid and details
 */
export const validateWordCount = (bodyWords, formalCriteria) => {
  const bounds = wordCountBounds(formalCriteria);
  if (!bounds) {
    return { isValid: true, reason: null };
  }

  const { min: minWordcount, max: maxWordcount, target: targetWordcount } = bounds;

  if (bodyWords > maxWordcount || bodyWords < minWordcount) {
    return {
      isValid: false,
      reason: 'wordcount_limits',
      details: {
        current: bodyWords,
        target: targetWordcount,
        min: minWordcount,
        max: maxWordcount,
      },
    };
  }

  return { isValid: true, reason: null };
};

/**
 * Validates if the submission contains valid references
 * @param {string} text - HTML text to validate
 * @returns {Object} Validation result with isValid and details
 */
export const validateReferences = (text) => {
  const { headline } = splitSubmission(text);

  if (!headline) {
    return {
      isValid: false,
      reason: 'no_references',
      details: {
        validReferences: validReferencesHeadlineTexts,
      },
    };
  }

  return { isValid: true, reason: null };
};

/**
 * Checks reference quality: APA style composition and in-text citations
 * @param {string} bodyHtml - Body HTML
 * @param {string} refsHtml - References HTML
 * @returns {Array} List of issues found
 */
export const checkReferenceQuality = (bodyHtml, refsHtml) => {
  const issues = [];
  if (!refsHtml) return issues;

  const plainBody = bodyHtml.replace(/<[^>]*>/g, ' ');

  // Extract reference lines and check quality/citations
  // We split by common block/break tags to get individual references
  const refLines = refsHtml
    .split(/<br\s*\/?>|<\/p>|<\/li>|<h[1-3][^>]*>|<\/h[1-3]>|<div>|<\/div>/i)
    .map((line) => line.replace(/<[^>]*>/g, '').trim())
    .filter((line) => line.length > 0);

  let apaCount = 0;
  let otherCount = 0;

  refLines.forEach((plainRef) => {
    // Regex for APA author-year: Müller, J. (2023) or Müller (2023)
    const apaMatch = plainRef.match(/^([A-Z][a-zA-Z\u00C0-\u017F-]+).*?\((\d{4})\)/);

    if (apaMatch) {
      apaCount += 1;
      const author = apaMatch[1];
      // Search for author in body (case insensitive)
      // We don't use \b because it fails with non-ASCII characters
      // instead we check if it's surrounded by non-alphanumeric or start/end
      const authorRegex = new RegExp(`(^|[^a-zA-Z\u00C0-\u017F-])${author}([^a-zA-Z\u00C0-\u017F-]|$)`, 'i');
      if (!authorRegex.test(plainBody)) {
        issues.push({ type: 'refs-missing-citation', details: { author } });
      }
    } else if (!plainRef.startsWith('http') && plainRef.length > 10) {
      // Doesn't look like APA and isn't just a URL, and is long enough to be non-citation content
      otherCount += 1;
    }
  });

  if (otherCount > 0) {
    issues.push({ type: 'refs-check-apa-style', details: { apaCount, otherCount } });
  }

  return issues;
};

/**
 * Comprehensive submission validation that runs all checks
 * @param {string} text - HTML text to validate
 * @param {number} bodyWords - Current body word count
 * @param {Object} formalCriteria - Formal criteria object
 * @returns {Object} Complete validation result
 */
export const validateSubmission = (text, bodyWords, formalCriteria) => {
  const validations = [];

  // Check for images
  if (containsImages(text)) {
    validations.push({
      type: 'images',
      isValid: false,
      reason: 'contains_images',
    });
  }

  // Check wordcount
  const wordCountValidation = validateWordCount(bodyWords, formalCriteria);
  if (!wordCountValidation.isValid) {
    validations.push({
      type: 'wordcount',
      ...wordCountValidation,
    });
  }

  // Check references and quality
  if ((formalCriteria?.minInTextCitations || 0) > 0) {
    const referencesValidation = validateReferences(text);
    if (!referencesValidation.isValid) {
      validations.push({
        type: 'references',
        ...referencesValidation,
      });
    } else {
      // Only check quality if references headline exists
      const { body, refs } = splitSubmission(text);
      const qualityIssues = checkReferenceQuality(body, refs);
      qualityIssues.forEach((issue) => {
        validations.push({
          ...issue,
          isValid: false,
        });
      });
    }
  }

  return {
    isValid: validations.length === 0,
    validations,
    hasErrors: validations.some((v) => v.type === 'images'),
    hasWarnings: validations.some((v) => v.type !== 'images'),
  };
};

/**
 * Creates validation dialog configurations for different validation types
 * @param {Object} validation - Validation result object
 * @param {Function} t - Translation function
 * @returns {Object} Dialog configuration
 */
export const createValidationDialog = (validation, t) => {
  switch (validation.type) {
    case 'images':
      return {
        title: t('student.task.composer.messages.submission-save-error-noimage-dialog.title'),
        message: t('student.task.composer.messages.submission-save-error-noimage-dialog.message'),
        buttons: [{ text: t('common.buttons.ok') }],
        isError: true,
      };

    default:
      return null;
  }
};

/**
 * Prompts user for validation confirmations and returns whether to proceed
 * @param {Array} validations - Array of validation results
 * @param {Function} showDialog - Dialog function
 * @param {Function} t - Translation function
 * @returns {Promise<boolean>} Whether to proceed with submission
 */
export const promptValidationConfirmations = async (validations, showDialog, t) => {
  // Handle errors first (these block submission)
  const errorValidations = validations.filter((v) => v.type === 'images');
  if (errorValidations.length > 0) {
    const dialog = createValidationDialog(errorValidations[0], t);
    showDialog(dialog);
    return false;
  }

  // Handle warnings (consolidated into one dialog)
  const warningValidations = validations.filter((v) => v.type !== 'images');

  if (warningValidations.length === 0) {
    return true;
  }

  const getWarningMessage = (v) => {
    switch (v.type) {
      case 'wordcount': {
        const isTooSmall = v.details.current < v.details.min;
        const key = isTooSmall ? 'wordcount-too-small' : 'wordcount-too-large';
        return t(`student.task.composer.validation.${key}`, {
          wordcount: v.details.current,
          min: v.details.min,
          max: v.details.max,
        });
      }
      case 'references':
        return t('student.task.composer.validation.no-references');
      case 'refs-missing-citation':
        return t('student.task.composer.validation.refs-missing-citation', { author: v.details.author });
      case 'refs-check-apa-style': {
        const apaCount = v.details?.apaCount ?? 0;
        const otherCount = v.details?.otherCount ?? 0;
        if (apaCount > 0 && otherCount > 0) {
          const apaClause = t('student.task.composer.validation.refs-composition-apa', { count: apaCount });
          const otherClause = t('student.task.composer.validation.refs-composition-other', { count: otherCount });
          return t('student.task.composer.validation.refs-check-composition', { apaClause, otherClause });
        }
        if (otherCount === 1) {
          return t('student.task.composer.validation.refs-check-apa-style');
        }
        return t('student.task.composer.validation.refs-check-apa-style-multiple', { otherCount });
      }
      default:
        return null;
    }
  };

  const result = await new Promise((resolve) => {
    showDialog({
      title: t('student.task.composer.submission-review-dialog.title'),
      message: '', // Clear default message ("Are you sure?")
      children: (
        <VStack space="md" mt="$2">
          <Text size="sm" color="$textBody" lineHeight="$md" fontWeight="$medium">
            {t('student.task.composer.submission-review-dialog.intro')}
          </Text>
          <VStack space="sm" px="$4">
            {warningValidations.map((v) => {
              const msg = getWarningMessage(v);
              if (!msg) return null;
              const warningKey = `${v.type}-${JSON.stringify(v.details ?? {})}`;
              return (
                <HStack key={warningKey} space="sm" alignItems="flex-start" py="$1">
                  <Icon as={AlertCircle} size="xs" color="$textHint" mt="$1" />
                  <Text size="xs" color="$textBody" flex={1} lineHeight="$md">
                    {msg}
                  </Text>
                </HStack>
              );
            })}
          </VStack>
        </VStack>
      ),
      buttons: [
        {
          text: t('student.task.composer.submission-review-dialog.button-submit'),
          action: 'secondary',
          variant: 'outline',
          onPress: () => resolve('continue'),
        },
        {
          text: t('student.task.composer.submission-review-dialog.button-edit'),
          action: 'primary',
          variant: 'solid',
          onPress: () => resolve('cancel'),
        },
      ],
    });
  });

  return result === 'continue';
};
